import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, KanbanSquare, Sparkles, LifeBuoy, Check, Archive, Save,
  History, X, Reply, UserPlus, Trash2,
} from "lucide-react";
import api from "@/lib/api";
import { Card, Spinner } from "@/components/common";
import PaketPruefung from "@/components/PaketPruefung";
import { StatusPill, TypePill, PRIORITIES, fmtDate, inputCls } from "./leadUtils";
import { useI18n } from "@/context/I18nContext";

export default function LeadDetail() {
  const { t } = useI18n();
  const { id } = useParams();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [edit, setEdit] = useState({});
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null); // 'task' | 'offer' | 'support'
  const [confirmDel, setConfirmDel] = useState(false);

  const load = () => api.get(`/leads/${id}`).then((r) => { setLead(r.data); setEdit(r.data); });
  useEffect(() => {
    load();
    api.get("/leads/config").then((r) => setCfg(r.data)).catch(() => {});
  }, [id]);

  const procKeys = useMemo(() => cfg?.project_types?.[lead?.project_type]?.process || [], [cfg, lead]);
  const resKeys = useMemo(() => cfg?.project_types?.[lead?.project_type]?.result || [], [cfg, lead]);

  if (!lead || !cfg) return <Spinner />;
  const plabel = (k) => t(cfg.process_labels[k] || k);
  const rlabel = (k) => t(cfg.result_labels[k] || k);

  const set = (k, v) => setEdit((e) => ({ ...e, [k]: v }));

  const doDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/leads/${id}`);
      toast.success(t("Anfrage gelöscht"));
      nav("/admin/leads");
    } catch (e) { toast.error(e.response?.data?.detail || t("Löschen fehlgeschlagen.")); setSaving(false); }
  };

  const saveField = async (fields) => {
    setSaving(true);
    try { const { data } = await api.put(`/leads/${id}`, fields); setLead(data); setEdit(data); toast.success(t("Gespeichert")); }
    catch (e) { toast.error(e.response?.data?.detail || t("Konnte nicht speichern.")); }
    finally { setSaving(false); }
  };

  const saveDetails = () => saveField({
    name: edit.name, company: edit.company ?? "", email: edit.email ?? "", phone: edit.phone ?? "", subject: edit.subject ?? "",
    message: edit.message ?? "", source: edit.source ?? "", campaign: edit.campaign ?? "", interest: edit.interest ?? "",
    brevo_contact_id: edit.brevo_contact_id ?? "", brevo_list: edit.brevo_list ?? "",
    callback_wanted: edit.callback_wanted ?? "", due_date: edit.due_date || null, assignee: edit.assignee ?? "", next_action: edit.next_action ?? "",
  });
  const saveMeta = () => saveField({
    status: edit.status, result_status: edit.result_status, priority: edit.priority,
    possible_service: edit.possible_service ?? "", estimated_value: edit.estimated_value === "" || edit.estimated_value == null ? null : Number(edit.estimated_value),
    internal_note: edit.internal_note ?? "",
  });

  return (
    <div className="fade-in max-w-6xl" data-testid="lead-detail">
      <button onClick={() => nav("/admin/leads")} data-testid="lead-back" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-4">
        <ArrowLeft className="w-4 h-4" /> {t("Zurück zu den Anfragen")}
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-black tracking-tight">{lead.name}</h1>
            <StatusPill status={lead.status} label={plabel(lead.status)} testid="lead-detail-status-badge" />
            <TypePill type={lead.lead_type} label={t(lead.lead_type_label || lead.lead_type)} />
          </div>
          <p className="text-sm text-gray-400 mt-1">{lead.company_name} · {lead.project_label}{lead.source ? ` · ${t("Quelle: {x}").replace("{x}", lead.source)}` : ""}{lead.campaign ? ` · ${t("Kampagne: {x}").replace("{x}", lead.campaign)}` : ""} · {t("Anfrage-ID")} <span className="font-mono text-gray-500">{lead.id.slice(0, 8)}</span></p>
        </div>
        {confirmDel ? (
          <div className="flex items-center gap-2" data-testid="ld-delete-confirm-box">
            <span className="text-sm text-red-300">{t("Anfrage endgültig löschen?")}</span>
            <button onClick={doDelete} disabled={saving} data-testid="ld-delete-confirm"
              className="bg-red-500/15 border border-red-400/40 text-red-300 hover:bg-red-500/25 font-bold py-2 px-3 rounded-lg text-sm disabled:opacity-60">{t("Ja, löschen")}</button>
            <button onClick={() => setConfirmDel(false)} data-testid="ld-delete-cancel"
              className="text-gray-400 hover:text-white font-semibold py-2 px-3 rounded-lg text-sm">{t("Abbrechen")}</button>
          </div>
        ) : (
          <button onClick={() => setConfirmDel(true)} data-testid="ld-delete"
            className="inline-flex items-center gap-2 border border-white/10 bg-white/5 hover:bg-red-500/10 hover:border-red-400/40 hover:text-red-300 text-gray-400 font-semibold py-2 px-3 rounded-lg text-sm">
            <Trash2 className="w-4 h-4" /> {t("Löschen")}
          </button>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Links: Daten + Verlauf */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <h3 className="font-bold mb-4">{t("Anfrage-Daten")}</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <F t={t("Name")}><input value={edit.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="ld-name" className={inputCls} /></F>
              <F t={t("Zuständige Person")}><input value={edit.assignee || ""} onChange={(e) => set("assignee", e.target.value)} className={inputCls} /></F>
              <F t={t("E-Mail")}><input value={edit.email || ""} onChange={(e) => set("email", e.target.value)} className={inputCls} /></F>
              <F t={t("Telefon")}><input value={edit.phone || ""} onChange={(e) => set("phone", e.target.value)} className={inputCls} /></F>
              <F t={t("Betreff")} full><input value={edit.subject || ""} onChange={(e) => set("subject", e.target.value)} data-testid="ld-subject" className={inputCls} /></F>
              <F t={t("Nachricht")} full><textarea value={edit.message || ""} onChange={(e) => set("message", e.target.value)} rows={4} className={inputCls} /></F>
              <F t={t("Quelle")}><input value={edit.source || ""} onChange={(e) => set("source", e.target.value)} className={inputCls} /></F>
              <F t={t("Gewünschter Rückruf")}><input value={edit.callback_wanted || ""} onChange={(e) => set("callback_wanted", e.target.value)} className={inputCls} /></F>
              <F t={t("Fälligkeit")}><input type="date" value={(edit.due_date || "").slice(0, 10)} onChange={(e) => set("due_date", e.target.value)} className={inputCls} /></F>
              <F t={t("Nächste Aktion")}><input value={edit.next_action || ""} onChange={(e) => set("next_action", e.target.value)} className={inputCls} placeholder={t("z. B. Offerte senden")} /></F>
              {!lead.client_id && <F t={t("Firma / Interessent")}><input value={edit.company || ""} onChange={(e) => set("company", e.target.value)} data-testid="ld-company" className={inputCls} /></F>}
              <F t={t("Kampagne")}><input value={edit.campaign || ""} onChange={(e) => set("campaign", e.target.value)} className={inputCls} placeholder={t("Brevo / Marketing")} /></F>
              <F t={t("Interesse")}><input value={edit.interest || ""} onChange={(e) => set("interest", e.target.value)} className={inputCls} /></F>
              {(lead.source === "Brevo" || edit.brevo_contact_id) && <F t={t("Brevo-Kontakt-ID")}><input value={edit.brevo_contact_id || ""} onChange={(e) => set("brevo_contact_id", e.target.value)} className={inputCls} /></F>}
              {(lead.source === "Brevo" || edit.brevo_list) && <F t={t("Brevo-Liste")}><input value={edit.brevo_list || ""} onChange={(e) => set("brevo_list", e.target.value)} className={inputCls} /></F>}
            </div>
            <button onClick={saveDetails} disabled={saving} data-testid="ld-save-details"
              className="mt-4 inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-bold py-2 px-4 rounded-lg"><Save className="w-4 h-4" /> {t("Daten speichern")}</button>
          </Card>

          {/* Verbundene Objekte */}
          <div className="grid sm:grid-cols-3 gap-4">
            <LinkCard icon={KanbanSquare} title={t("Aufgaben")} count={(lead.tasks || []).length} testid="ld-linked-tasks"
              items={(lead.tasks || []).map((tk) => tk.title)} emptyLabel={t("Keine verbunden.")} />
            <LinkCard icon={Sparkles} title={t("Angebot")} count={(lead.offers || []).length} testid="ld-linked-offers"
              items={(lead.offers || []).map((o) => o.title)} emptyLabel={t("Keine verbunden.")} />
            <LinkCard icon={LifeBuoy} title={t("Supportfall")} count={(lead.support || []).length} testid="ld-linked-support"
              items={(lead.support || []).map((s) => (s.message || "").slice(0, 40))} emptyLabel={t("Keine verbunden.")} />
          </div>

          {/* Verlauf */}
          <Card className="p-6">
            <h3 className="font-bold mb-4 flex items-center gap-2"><History className="w-4 h-4 text-[#A3E635]" /> {t("Verlauf / Aktivitäten")}</h3>
            {(lead.activity || []).length === 0 ? <p className="text-sm text-gray-500">{t("Noch keine Aktivitäten.")}</p> : (
              <div className="space-y-3" data-testid="ld-activity">
                {lead.activity.map((a) => (
                  <div key={a.id} className="flex gap-3 text-sm border-b border-white/5 pb-3 last:border-0">
                    <div className="w-2 h-2 rounded-full bg-[#A3E635] mt-1.5 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium">{a.action}</div>
                      {a.changes && <div className="text-xs text-gray-500 mt-0.5">{renderChanges(a.changes)}</div>}
                      <div className="text-[11px] text-gray-600 mt-0.5">{a.actor_name} · {fmtDate(a.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Rechts: Status + Aktionen */}
        <div className="space-y-4 lg:sticky lg:top-6 self-start">
          <Card className="p-5">
            <h3 className="font-bold mb-4">{t("Status & Auswertung")}</h3>
            <F t={t("Bearbeitungsstatus")}>
              <select value={edit.status} onChange={(e) => set("status", e.target.value)} data-testid="ld-status" className={inputCls}>
                {procKeys.map((s) => <option key={s} value={s}>{plabel(s)}</option>)}
              </select>
            </F>
            <div className="mt-3">
              <F t={t("Interner Ergebnisstatus (nur KEWAG)")}>
                <select value={edit.result_status || "noch_offen"} onChange={(e) => set("result_status", e.target.value)} data-testid="ld-result" className={inputCls}>
                  {resKeys.map((s) => <option key={s} value={s}>{rlabel(s)}</option>)}
                </select>
              </F>
            </div>
            <div className="mt-3"><F t={t("Priorität")}>
              <select value={edit.priority || "mittel"} onChange={(e) => set("priority", e.target.value)} data-testid="ld-priority" className={inputCls}>
                {PRIORITIES.map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}
              </select>
            </F></div>
            <div className="mt-3"><F t={t("Mögliche Leistung (intern)")}><input value={edit.possible_service || ""} onChange={(e) => set("possible_service", e.target.value)} className={inputCls} /></F></div>
            <div className="mt-3"><F t={t("Geschätzter Wert CHF (intern)")}><input type="number" min="0" value={edit.estimated_value ?? ""} onChange={(e) => set("estimated_value", e.target.value)} className={inputCls} /></F></div>
            <div className="mt-3"><F t={t("Interne Notiz (nur KEWAG)")}><textarea value={edit.internal_note || ""} onChange={(e) => set("internal_note", e.target.value)} rows={3} className={inputCls} /></F></div>
            <button onClick={saveMeta} disabled={saving} data-testid="ld-save-meta"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 rounded-lg"><Save className="w-4 h-4" /> {t("Status & Auswertung speichern")}</button>
          </Card>

          <Card className="p-5 space-y-2.5">
            <h3 className="font-bold mb-1">{t("Aktionen")}</h3>
            <ActBtn icon={Reply} testid="ld-act-reply" onClick={() => saveField({ status: "in_bearbeitung" })}>{t("Antwort vorbereiten (In Bearbeitung)")}</ActBtn>
            <ActBtn icon={KanbanSquare} testid="ld-act-task" onClick={() => setModal("task")}>{t("Aufgabe erstellen")}</ActBtn>
            <ActBtn icon={Sparkles} testid="ld-act-offer" onClick={() => setModal("offer")}>{t("Angebot vorbereiten")}</ActBtn>
            {(lead.lead_type === "neukunden_lead" || !lead.client_id) && (
              <ActBtn icon={UserPlus} testid="ld-act-create-client" accent onClick={() => nav(`/admin/clients/new?from_lead=${lead.id}`)}>{t("Kunde aus Lead erstellen")}</ActBtn>
            )}
            <ActBtn icon={LifeBuoy} testid="ld-act-support" onClick={() => setModal("support")}>{t("Supportfall erstellen")}</ActBtn>
            <ActBtn icon={Check} testid="ld-act-done" accent onClick={() => saveField({ status: "erledigt" })}>{t("Erledigt markieren")}</ActBtn>
            <ActBtn icon={Archive} testid="ld-act-archive" onClick={() => saveField({ status: "archiviert" })}>{t("Archivieren")}</ActBtn>
          </Card>

          <PaketPruefung entityType="lead" entity={lead} onChanged={load} />
        </div>
      </div>

      {modal === "task" && <TaskModal lead={lead} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {modal === "offer" && <OfferModal lead={lead} services={cfg.offer_services} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {modal === "support" && <SupportModal lead={lead} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
    </div>
  );
}

function renderChanges(c) {
  try {
    if (c.status) return `${c.status.von} → ${c.status.zu}`;
    if (c.ergebnis) return `${c.ergebnis.von} → ${c.ergebnis.zu}`;
    if (c.aufgabe) return `Aufgabe: ${c.aufgabe}`;
    if (c.leistungen) return `Leistungen: ${(c.leistungen || []).join(", ")}`;
    if (c.felder) return `Felder: ${(c.felder || []).join(", ")}`;
    if (c.betreff) return c.betreff;
    return "";
  } catch { return ""; }
}

const F = ({ t, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{t}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const ActBtn = ({ icon: Icon, children, onClick, testid, accent }) => (
  <button onClick={onClick} data-testid={testid}
    className={`w-full text-left inline-flex items-center gap-2.5 py-2.5 px-3 rounded-lg text-sm font-semibold transition-colors ${accent ? "bg-[#A3E635]/10 hover:bg-[#A3E635]/20 text-[#A3E635]" : "bg-white/5 hover:bg-white/10 text-gray-200"}`}>
    <Icon className="w-4 h-4 shrink-0" /> {children}
  </button>
);

const LinkCard = ({ icon: Icon, title, count, items, testid, emptyLabel }) => (
  <Card className="p-4" data-testid={testid}>
    <div className="flex items-center gap-2 mb-2"><Icon className="w-4 h-4 text-gray-400" /><span className="text-sm font-bold">{title}</span><span className="ml-auto text-xs text-gray-500">{count}</span></div>
    {count === 0 ? <p className="text-xs text-gray-600">{emptyLabel}</p> : (
      <ul className="space-y-1">{items.slice(0, 4).map((it, i) => <li key={i} className="text-xs text-gray-400 truncate">• {it}</li>)}</ul>
    )}
  </Card>
);

// ---------- Modals ----------
const ModalShell = ({ title, icon: Icon, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto bg-black/70 backdrop-blur-sm" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <div className="w-full max-w-lg bg-[#121217] border border-white/10 rounded-2xl p-6 my-8">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2"><Icon className="w-5 h-5 text-[#A3E635]" /><h3 className="text-lg font-bold tracking-tight">{title}</h3></div>
        <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/5"><X className="w-5 h-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

function TaskModal({ lead, onClose, onDone }) {
  const { t } = useI18n();
  const [title, setTitle] = useState(lead.subject || lead.message || "");
  const [priority, setPriority] = useState(lead.priority || "mittel");
  const [due, setDue] = useState("");
  const [setStatus, setSetStatus] = useState("in_bearbeitung");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post(`/leads/${lead.id}/convert-task`, { title, priority, due_date: due || null, set_status: setStatus }); toast.success(t("Aufgabe erstellt")); onDone(); }
    catch { toast.error(t("Fehler beim Erstellen.")); setBusy(false); }
  };
  return (
    <ModalShell title={t("Aufgabe aus Anfrage erstellen")} icon={KanbanSquare} onClose={onClose}>
      <div className="space-y-3">
        <F t={t("Titel")}><input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="task-modal-title" className={inputCls} /></F>
        <div className="grid grid-cols-2 gap-3">
          <F t={t("Priorität")}><select value={priority} onChange={(e) => setPriority(e.target.value)} className={inputCls}>{PRIORITIES.map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}</select></F>
          <F t={t("Fälligkeit")}><input type="date" value={due} onChange={(e) => setDue(e.target.value)} className={inputCls} /></F>
        </div>
        <F t={t("Anfrage danach setzen auf")}>
          <select value={setStatus} onChange={(e) => setSetStatus(e.target.value)} className={inputCls}>
            <option value="in_bearbeitung">{t("In Bearbeitung")}</option><option value="erledigt">{t("Erledigt")}</option>
          </select>
        </F>
        <p className="text-[11px] text-gray-500">{t("Kunde, Kontakt und Priorität werden übernommen. Ergebnisstatus intern: „In Aufgabe umgewandelt\".")}</p>
      </div>
      <div className="flex justify-end gap-3 mt-6">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{t("Abbrechen")}</button>
        <button onClick={go} disabled={busy} data-testid="task-modal-confirm" className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2 px-5 rounded-lg disabled:opacity-50">{busy ? "…" : t("Aufgabe erstellen")}</button>
      </div>
    </ModalShell>
  );
}

function OfferModal({ lead, services, onClose, onDone }) {
  const { t } = useI18n();
  const [sel, setSel] = useState(lead.possible_service ? [lead.possible_service] : []);
  const [note, setNote] = useState("");
  const [value, setValue] = useState(lead.estimated_value ?? "");
  const [busy, setBusy] = useState(false);
  const toggle = (s) => setSel((x) => (x.includes(s) ? x.filter((y) => y !== s) : [...x, s]));
  const go = async () => {
    setBusy(true);
    try { await api.post(`/leads/${lead.id}/convert-offer`, { services: sel, note, estimated_value: value === "" ? null : Number(value) }); toast.success(t("Angebot vorbereitet")); onDone(); }
    catch { toast.error(t("Fehler beim Vorbereiten.")); setBusy(false); }
  };
  return (
    <ModalShell title={t("Angebot vorbereiten (intern)")} icon={Sparkles} onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{t("Leistungen (nur intern für KEWAG)")}</label>
          <div className="grid grid-cols-2 gap-2 mt-2" data-testid="offer-modal-services">
            {services.map((s) => (
              <button key={s} type="button" onClick={() => toggle(s)} data-testid={`offer-svc-${s}`}
                className={`text-left text-xs px-3 py-2 rounded-lg border transition-colors ${sel.includes(s) ? "border-[#A3E635] bg-[#A3E635]/10 text-[#A3E635]" : "border-white/10 text-gray-300 hover:border-white/20"}`}>{s}</button>
            ))}
          </div>
        </div>
        <F t={t("Notiz")}><textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className={inputCls} placeholder={t("Interne Notiz zum Angebot...")} /></F>
        <F t={t("Geschätzter Wert CHF")}><input type="number" min="0" value={value} onChange={(e) => setValue(e.target.value)} data-testid="offer-modal-value" className={inputCls} /></F>
        <p className="text-[11px] text-gray-500">{t("Erscheint unter „Nächste Schritte\" (intern) und zählt im Dashboard. Ergebnisstatus intern: „In Angebot umgewandelt\". Nicht für den Kunden sichtbar.")}</p>
      </div>
      <div className="flex justify-end gap-3 mt-6">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{t("Abbrechen")}</button>
        <button onClick={go} disabled={busy} data-testid="offer-modal-confirm" className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2 px-5 rounded-lg disabled:opacity-50">{busy ? "…" : t("Angebot vorbereiten")}</button>
      </div>
    </ModalShell>
  );
}

function SupportModal({ lead, onClose, onDone }) {
  const { t } = useI18n();
  const [msg, setMsg] = useState(t("Supportfall aus Anfrage von {name}: {subject}").replace("{name}", lead.name).replace("{subject}", lead.subject || ""));
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post(`/leads/${lead.id}/convert-support`, { message: msg }); toast.success(t("Supportfall erstellt")); onDone(); }
    catch { toast.error(t("Fehler beim Erstellen.")); setBusy(false); }
  };
  return (
    <ModalShell title={t("Supportfall aus Anfrage erstellen")} icon={LifeBuoy} onClose={onClose}>
      <F t={t("Nachricht")}><textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={4} data-testid="support-modal-msg" className={inputCls} /></F>
      <p className="text-[11px] text-gray-500 mt-2">{t("Legt einen Eintrag im Support des Kunden an. Ergebnisstatus intern: „In Supportfall umgewandelt\".")}</p>
      <div className="flex justify-end gap-3 mt-6">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{t("Abbrechen")}</button>
        <button onClick={go} disabled={busy} data-testid="support-modal-confirm" className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2 px-5 rounded-lg disabled:opacity-50">{busy ? "…" : t("Supportfall erstellen")}</button>
      </div>
    </ModalShell>
  );
}
